// src/components/SettingsPage.js

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { templates, renderGeometricAvatar } from '../../utils/avatar';
import './SettingsPage.css';

Modal.setAppElement('#root');

export const SettingsPage = ({ isOpen, onRequestClose }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [tempDisplayName, setTempDisplayName] = useState('');
  const [avatarTemplate, setAvatarTemplate] = useState(0);
  const [tempAvatarTemplate, setTempAvatarTemplate] = useState(0);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        if (currentUser) {
          const name = currentUser.user_metadata?.display_name || currentUser.email.split('@')[0];
          setDisplayName(name);
          setTempDisplayName(name);
          const tIdx = currentUser.user_metadata?.avatar_template || 0;
          setAvatarTemplate(tIdx);
          setTempAvatarTemplate(tIdx);
        }
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('body-scroll-locked');
    } else {
      document.body.classList.remove('body-scroll-locked');
    }
    return () => {
      document.body.classList.remove('body-scroll-locked');
    };
  }, [isOpen]);

  const handleEditClick = () => {
    setTempDisplayName(displayName);
    setTempAvatarTemplate(avatarTemplate);
    setIsEditNameOpen(true);
  };

  const handleSaveProfileWrapper = async () => {
    if (!tempDisplayName.trim()) return toast.error("Name cannot be empty.");
    setIsSavingProfile(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { 
          display_name: tempDisplayName.trim(),
          avatar_template: tempAvatarTemplate
        }
      });
      if (error) throw error;
      toast.success("Profile updated successfully!");
      setDisplayName(tempDisplayName.trim());
      setAvatarTemplate(tempAvatarTemplate);
      setIsEditNameOpen(false);
      
      // Update session locally to sync display name instantly in Navbar
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
    } catch (error) {
      toast.error(`Failed to update profile: ${error.message}`);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user || !user.email) {
      return toast.error("Could not find user email.");
    }
    const toastId = toast.loading("Sending password reset email...");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email);
      if (error) throw error;
      toast.dismiss(toastId);
      toast.success(`Password reset link sent to ${user.email}`);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      toast.dismiss(toastId);
      toast.error(`Failed to send email: ${error.message}`);
    }
  };

  const handleDeleteAccount = () => {
    if (!user) {
      return toast.error("No user is currently logged in.");
    }
    const proceedWithDeletion = async () => {
      const toastId = toast.loading("Deleting your account and data...");
      try {
        // 1. Fetch user's files to clean up storage
        const { data: userFiles, error: fetchError } = await supabase
          .from('files')
          .select('fileUrl')
          .eq('uid', user.id);

        if (!fetchError && userFiles && userFiles.length > 0) {
          const storagePaths = userFiles
            .map(f => {
              const urlParts = f.fileUrl.split('/public/files/');
              return urlParts.length === 2 ? urlParts[1] : null;
            })
            .filter(Boolean);

          if (storagePaths.length > 0) {
            await supabase.storage.from('files').remove(storagePaths);
          }
        }

        // 2. Delete user account from database & auth (triggers ON DELETE CASCADE for files/sharedLinks)
        const { error: rpcError } = await supabase.rpc('delete_user');
        if (rpcError) throw rpcError;

        toast.dismiss(toastId);
        toast.success("Your account and all associated data have been permanently deleted.");
        onRequestClose();
        
        // 3. Sign out of active session
        await supabase.auth.signOut();
        navigate('/login');
      } catch (error) {
        console.error("Account deletion error:", error);
        toast.dismiss(toastId);
        toast.error(`Deletion failed: ${error.message}`);
      }
    };
    toast((t) => (
      <div className="confirmation-toast">
        <h4>Permanently delete your account?</h4>
        <p>This action is irreversible. All your files and data will be permanently deleted.</p>
        <div className="confirmation-buttons">
          <button
            className="confirm-delete-button"
            onClick={() => {
              toast.dismiss(t.id);
              proceedWithDeletion();
            }}
          >
            Yes, Delete My Account
          </button>
          <button className="cancel-button" onClick={() => toast.dismiss(t.id)}>
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000, className: 'premium-toast-card' });
  };

  if (!user) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Settings Drawer"
      className="settings-drawer-content"
      overlayClassName="overlay"
    >
      <button className="modal-close-btn" onClick={onRequestClose} aria-label="Close Settings">&times;</button>
      
      <div className="settings-header">
        <h2>Settings</h2>
      </div>

      <div className="settings-scroll-area">
        {/* Profile Card */}
        <div className="settings-card">
          <div className="profile-card-header">
            <h3>Profile</h3>
            <button className="profile-edit-btn" onClick={handleEditClick}>Edit</button>
          </div>
          
          <div className="profile-card-body">
            <div className="profile-info-row">
              {renderGeometricAvatar(avatarTemplate, 56, '-settings')}
              <div className="profile-meta">
                <span className="profile-name-value">{displayName}</span>
                <span className="profile-email-value">{user.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Card */}
        <div className="settings-card">
          <h3>Security</h3>
          <div className="setting-item">
            <div>
              <label>Change Password</label>
              <p>Send password reset link to your email.</p>
            </div>
            <button className="action-button" onClick={handlePasswordReset}>
              Send Email
            </button>
          </div>
        </div>
        
        {/* Danger Zone Card */}
        <div className="settings-card danger-zone">
          <h3>Danger Zone</h3>
          <div className="setting-item">
            <div>
              <label>Delete Account</label>
              <p>Permanently delete your account and all associated files.</p>
            </div>
            <button className="delete-button" onClick={handleDeleteAccount}>
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Pop-up modal for changing display name */}
      <Modal
        isOpen={isEditNameOpen}
        onRequestClose={() => setIsEditNameOpen(false)}
        contentLabel="Edit Name Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '400px' } }}
      >
        <button className="modal-close-btn" onClick={() => setIsEditNameOpen(false)} aria-label="Close edit name">&times;</button>
        <h2>Change Display Name</h2>

        {/* Real-time Dynamic Vector Avatar Preview */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          {renderGeometricAvatar(tempAvatarTemplate, 90, '-preview')}
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
          <label style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', color: 'var(--color-text-secondary)', letterSpacing: '0.8px', paddingLeft: '4px' }}>Display Name</label>
          <input 
            type="text" 
            className="profile-input"
            value={tempDisplayName} 
            onChange={(e) => setTempDisplayName(e.target.value)}
            placeholder="Enter display name"
            style={{ width: '100%', padding: '12px 16px', border: '1px solid var(--color-border)', borderRadius: '12px', fontSize: '14px', outline: 'none', backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}
          />
        </div>

        {/* Circular Color Theme Deck Selector */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', color: 'var(--color-text-secondary)', letterSpacing: '0.8px', display: 'block', marginBottom: '12px', paddingLeft: '4px' }}>Choose Avatar Theme</label>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center', paddingLeft: '4px' }}>
            {templates.map((t, idx) => {
              const isSelected = tempAvatarTemplate === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setTempAvatarTemplate(idx)}
                  className={`avatar-theme-dot ${isSelected ? 'selected' : ''}`}
                  style={{
                    backgroundColor: t.bg,
                    width: '32px',
                    height: '32px'
                  }}
                  title={t.name}
                >
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: `linear-gradient(135deg, ${t.leftHead}, ${t.rightHeadBg})` }} />
                </button>
              );
            })}
          </div>
        </div>

        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button 
            className="action-button save-profile-btn" 
            onClick={handleSaveProfileWrapper}
            disabled={isSavingProfile}
            style={{ padding: '12px 24px', borderRadius: '12px', backgroundColor: 'var(--color-primary)', color: 'var(--color-background)', border: 'none', fontSize: '13px', fontWeight: '700', cursor: 'pointer', margin: 0 }}
          >
            {isSavingProfile ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </Modal>
    </Modal>
  );
};